import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import {
  Box,
  IconButton,
  Popover,
  Badge,
  Typography,
  TextField,
  Autocomplete,
  List,
  ListItem,
  darken,
  lighten,
  Grid,
  Button,
  CircularProgress
} from '@mui/material';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { setActiveVersion, setTitleById } from '@/redux/slices/titleV2Slice';
import { selectActiveTitle, selectActiveVersion } from '@/redux/selectors/titleV2Selectors';
import { selectCanReadTitleCustomer, selectCanWriteTitleCustomer } from '@/redux/selectors/authSelectors';

import { useConfigValue } from '@/components/_hooks/useConfigValue';
import useConfirmationPrompt from '@/components/_hooks/useConfirmationPrompt';
import axios from '@/_utils/axios';
import { useToast } from '@/components/_common/Toast';
import { PolarisIcon, TitleTypeLabel } from '@/exports/columnCommonComponents';
import { isActiveVersionAVersion } from '@/redux/slices/slice_types';
import { ConfigContext } from '@/contextAPI/configContext';
import { capitalizeNoun, pluralizeNoun } from '@/_utils/utils';

const popoverProps = {
  anchorOrigin: {
    vertical: 'bottom' as const,
    horizontal: 'center' as const
  },
  transformOrigin: {
    vertical: 'top' as const,
    horizontal: 'right' as const
  }
};

type CustomerOption = { title: string; id: number };

const TitleCustomerAssignmentV2 = () => {
  const dispatch = useAppDispatch();
  const canReadTitleCustomers = useAppSelector(selectCanReadTitleCustomer);
  const canWriteTitleCustomers = useAppSelector(selectCanWriteTitleCustomer);
  const activeTitle = useAppSelector(selectActiveTitle);
  const activeVersion = useAppSelector(selectActiveVersion);
  const title = isActiveVersionAVersion(activeVersion) ? activeVersion : activeTitle;
  const [customersList, setCustomersList] = useState<{ [key: number]: string }>({});
  const [selectedCustomers, setSelectedCustomers] = useState<number[]>(title?.customers || []);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [inputValue, setInputValue] = useState<string>('');
  const [autocompleteOpen, setAutocompleteOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const customerOptions: CustomerOption[] = Object.keys(customersList).map((id) => ({ title: customersList[Number(id)], id: Number(id) }));
  const inputRef = useRef<HTMLInputElement>(null);
  const { error, success } = useToast();
  const titleCustomerLabel = useConfigValue(['titleConfig', 'customerLabel']) || 'customer';
  const pluralizedTitleCustomerLabel = pluralizeNoun(titleCustomerLabel);
  const isEnabled = useContext(ConfigContext).dataConfig?.titles?.enableTitleCustomerAssignment || false;

  if (!isEnabled) return null;

  const { dialog: confirmationPrompt, setDialogVisible } = useConfirmationPrompt({
    title: 'Unsaved Changes',
    titleIcon: <PolarisIcon icon="warning" height={19} style={{ opacity: 0.5, marginRight: 8 }} />,
    message: 'You have unsaved changes. Please save or discard your changes before closing.',
    onConfirm: () => {
      setSelectedCustomers(title?.customers || []);
      setAnchorEl(null);
      setAutocompleteOpen(false);
    }
  });

  useEffect(() => {
    if (!isEnabled) return;

    if (canReadTitleCustomers) {
      axios.post('/core/customers/search/?page_size=999&page_num=0').then((res) => {
        if (res.data?.data) {
          const customers = res.data.data.reduce((acc: { [key: number]: string }, customer: { id: number; name: string }) => {
            acc[customer.id] = customer.name;
            return acc;
          }, {});
          setCustomersList(customers);
        }
      });
    }
  }, []);

  useEffect(() => {
    setSelectedCustomers(title?.customers || []);
  }, [title?.customers]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (isSaving = false) => {
    if (!isSaving) {
      const sortedSelectedCustomers = [...selectedCustomers].sort();
      const sortedTitleCustomers = [...(title?.customers || [])].sort();

      if (JSON.stringify(sortedSelectedCustomers) !== JSON.stringify(sortedTitleCustomers)) {
        setDialogVisible(true);
        return;
      }
    }
    setAnchorEl(null);
    setAutocompleteOpen(false);
  };

  const onCustomerSelect = (_e: React.SyntheticEvent<Element, Event>, newValue: unknown) => {
    if (newValue && typeof newValue === 'object' && 'id' in newValue) {
      const selectedCustomerId = (newValue as CustomerOption).id;
      setSelectedCustomers((prev) => {
        if (prev.includes(selectedCustomerId)) {
          return prev.filter((id) => id !== selectedCustomerId);
        } else {
          return [...prev, selectedCustomerId];
        }
      });
      setInputValue('');
      if (inputRef.current) {
        inputRef.current.blur();
      }
    }
  };

  const handleDelete = (customerId: number) => {
    setSelectedCustomers(selectedCustomers.filter((id) => id !== customerId));
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setAutocompleteOpen(false);
      setInputValue('');
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setAutocompleteOpen(false);
    }
  };

  const handleSave = async () => {
    if (!title) {
      return;
    }

    setLoading(true);
    try {
      await axios.post(`inventory/titles/${title.id}/customers/assign/`, { customers: selectedCustomers });
      success(`${capitalizeNoun(titleCustomerLabel)} saved successfully.`);
      handleClose(true);
      if (isActiveVersionAVersion(activeVersion)) {
        dispatch(setActiveVersion({ ...activeVersion, customers: selectedCustomers }));
      } else if (activeTitle) {
        dispatch(setTitleById({ id: title.id, title: { ...activeTitle, customers: selectedCustomers } }));
      }
    } catch (err) {
      error(err || `Failed to save ${pluralizedTitleCustomerLabel}.`);
    } finally {
      setLoading(false);
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'customer-popover' : undefined;

  if (!canReadTitleCustomers) return null;
  return (
    <Box sx={{ ml: 1 }}>
      <IconButton color="primary" aria-describedby={id} onClick={handleClick}>
        <StyledBadge badgeContent={title?.customers?.length} color="secondary">
          <PolarisIcon icon="customer_assignment" height={40} />
        </StyledBadge>
      </IconButton>

      <StyledPopover id={id} open={open} anchorEl={anchorEl} onClose={() => handleClose(false)} {...popoverProps} disableScrollLock>
        {loading && (
          <LoadingOverlay>
            <CircularProgress />
          </LoadingOverlay>
        )}
        <Grid container alignItems="center" sx={{ mt: 1, mb: 3 }}>
          <PolarisIcon icon="customers" height={24} style={{ color: 'var(--default-main)' }} />
          <Typography sx={{ ml: 1 }}>
            {capitalizeNoun(pluralizedTitleCustomerLabel)} assigned to <TitleTypeLabel type={title?.entity_type} inverted sx={{ ml: 0.5 }} />:
          </Typography>
        </Grid>
        {canReadTitleCustomers && (
          <StyledAutocomplete
            id="customer-autocomplete"
            options={customerOptions}
            getOptionLabel={(option: any) => option.title || ''}
            onInputChange={(_e, newInputValue) => setInputValue(newInputValue)}
            onChange={onCustomerSelect}
            open={autocompleteOpen}
            onOpen={() => setAutocompleteOpen(true)}
            onClose={() => setAutocompleteOpen(false)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            inputValue={inputValue}
            value={selectedCustomers}
            filterSelectedOptions
            isOptionEqualToValue={(option: any, value: any) => value.includes(option.id)}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" placeholder={`Select ${pluralizedTitleCustomerLabel} to assign`} inputRef={inputRef} />
            )}
          />
        )}
        <SelectionContainer>
          {!selectedCustomers.length && <EmptyCustomersText>No {pluralizedTitleCustomerLabel} selected.</EmptyCustomersText>}
          {selectedCustomers.map((customerId: number) => {
            return (
              <ListItem
                key={customerId}
                secondaryAction={
                  canWriteTitleCustomers ? (
                    <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(customerId)}>
                      <PolarisIcon icon="close" height={20} />
                    </IconButton>
                  ) : null
                }
              >
                {customersList[customerId]}
              </ListItem>
            );
          })}
        </SelectionContainer>
        {canReadTitleCustomers && (
          <Grid container justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button variant="text" color="default" onClick={() => handleClose(false)} disabled={loading}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={loading} sx={{ ml: 1 }}>
              Save
            </Button>
          </Grid>
        )}
      </StyledPopover>
      {confirmationPrompt}
    </Box>
  );
};

export default TitleCustomerAssignmentV2;

const StyledPopover = styled(Popover)`
  .MuiPaper-root {
    padding: ${(props) => props.theme.spacing(3)};
    width: 450px;
    position: relative;
  }
`;

const StyledBadge = styled(Badge)`
  .MuiBadge-badge {
    top: 5px;
    right: 5px;
  }
`;

const StyledAutocomplete = styled(Autocomplete)`
  width: 400px;
`;

const SelectionContainer = styled(List)`
  display: flex;
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing(3)};
  max-height: 40vh;
  overflow-y: auto;

  .MuiListItem-root {
    background: ${({ theme: { palette } }) =>
      palette.mode === 'light' ? darken(palette.background.paper, 0.05) : lighten(palette.background.paper, 0.1)};
    margin: ${({ theme }) => theme.spacing(0.5, 0)};
    border-radius: ${({ theme }) => theme.shape.borderRadius}px;
    padding: ${({ theme }) => theme.spacing(1.5, 2)};
    border-left: 4px solid ${({ theme }) => theme.palette.primary.main};
    font-size: 1.1rem;
    &:hover {
      background: ${({ theme }) => lighten(theme.palette.background.paper, 0.08)};
    }
  }
`;

const EmptyCustomersText = styled(Typography)`
  color: ${({ theme }) => darken(theme.palette.text.primary, 0.5)};
  font-style: italic;
  margin-left: ${({ theme }) => theme.spacing(1)};
  width: 100%;
  min-height: 55px;
`;

const LoadingOverlay = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.palette.background.paper}8a;
  z-index: 1;
`;
